import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {isEqual} from 'lodash';
import React, {useEffect, useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {WEAVE_TRACE_PANEL_TYPE} from '../../../components/PanelWeaveTrace/common';
import {useSelector} from '../../../state/hooks';
import {usePanelComments} from '../../../state/reports/usePanelComments';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import {Ref as PanelBankSectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {getDndSortablePanelId} from '../../PanelBank/sectionLayout/utils';
import {useWorkspaceSectionContext} from '../../PanelBank/sectionLayout/WorkspaceSectionContextProvider';
import {useWorkspaceSectionLayoutConfigs} from '../../PanelBank/sectionLayout/WorkspaceSectionLayoutConfigs';
import {PanelCompRedux} from '../../PanelCompRedux';
import {useSectionSettingsContext} from '../../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useConfigXBounds} from '../hooks/hooks';
import {PanelContextProvider, usePanelContext} from '../PanelContextProvider';
import {PanelEditorContainer} from '../PanelEditor/PanelEditorContainer';
import {PanelIDContextProvider} from '../PanelIDContext';
import {SharedPanelStateContextProvider} from '../SharedPanelStateContext';
import {PanelPopupMode} from '../types';
import {usePanelViewTypeClass} from '../usePanelViewTypeClass';
import {useIsWorkspacePanelInView} from './useIsWorkspacePanelInView';
import {WorkspacePanelHeader} from './WorkspacePanelHeader';

type WorkspacePanelContainerProps = {
  isProjectReadOnly: boolean;
  panelBankSectionConfigRef: PanelBankSectionRef;
  panelRef: PanelRef;
};

export const WorkspacePanelContainer = ({
  isProjectReadOnly,
  panelBankSectionConfigRef,
  panelRef,
}: WorkspacePanelContainerProps) => {
  // This is currently used to track panel setting analytics
  const stablePanelId = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id]?.__id__
  );

  const {inheritedSettings} = useSectionSettingsContext();

  const {setNodeRef, transform, transition, isDragging} = useSortable({
    id: getDndSortablePanelId(panelRef),
    data: {
      // This helps indicate if we're dragging a section or panel
      type: 'panel',
      sectionRef: panelBankSectionConfigRef,
      panelRef: panelRef,
    },
    // disable this for smoother drop experience
    // https://github.com/clauderic/dnd-kit/issues/336#issuecomment-864564264
    animateLayoutChanges: () => false,
  });

  // When a panel is deleted, the stablePanelId will be undefined.
  // This is a workaround to avoid rendering a panel that doesn't exist.
  if (!stablePanelId) {
    return null;
  }

  return (
    <div
      ref={setNodeRef}
      className={twMerge(
        'relative h-full w-full',
        'line-plot-panel-crosshair-hover',
        isDragging ? 'opacity-60' : 'opacity-100'
      )}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}>
      <PanelContextProvider
        inheritedSettings={inheritedSettings}
        isProjectReadOnly={isProjectReadOnly}
        panelBankSectionConfigRef={panelBankSectionConfigRef}
        panelRef={panelRef}>
        <PanelIDContextProvider stablePanelId={stablePanelId}>
          <WorkspacePanel />
        </PanelIDContextProvider>
      </PanelContextProvider>
    </div>
  );
};

export const WorkspacePanel = () => {
  const {shouldRenderContent} = useWorkspaceSectionContext();
  const {isReadOnly, panelRef, panel, setPanelPopupMode} = usePanelContext();

  const location = useLocation();
  const panelId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('panelId');
  }, [location.search]);

  const configXBounds = useConfigXBounds(panelRef);

  const panelType = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].viewType
  );

  // Open panel editor only if panelId matches the current panel's ID
  useEffect(() => {
    if (panelId === panel.__id__) {
      setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
    } else {
      setPanelPopupMode(undefined);
    }
  }, [panelId, panel.__id__, setPanelPopupMode]);

  const {isHighlighted} = usePanelComments({panelRef});

  const {isDragging, over, active} = useSortable({
    id: getDndSortablePanelId(panelRef),
  });
  const isActivePanel = useMemo(() => {
    return panelRef.id === active?.id;
  }, [panelRef.id, active?.id]);

  const panelViewTypeClass = usePanelViewTypeClass(panelType);

  const {targetPanelRef} = useWorkspaceDrawerContext();
  const isWorkspaceDrawerTarget = useMemo(
    () => isEqual(panelRef, targetPanelRef),
    [panelRef, targetPanelRef]
  );

  const {panelWidth, panelHeight} = useWorkspaceSectionLayoutConfigs();

  const {ref: domRef, shouldMountPanel} = useIsWorkspacePanelInView();

  return (
    <SharedPanelStateContextProvider config={configXBounds}>
      <PanelEditorContainer currentHeight={0} />
      <div
        className={twMerge(
          'flex flex-col',
          'h-full w-full',
          'px-8 pb-12 pt-4',
          'bg-white',
          'rounded-[3px] border-[1px] border-moon-300',
          'transition-[box-shadow,margin-top] duration-300',
          // Panel type specific class
          panelViewTypeClass,
          // Conditional states
          !shouldRenderContent && 'opacity-50 [&>*]:hidden',
          isActivePanel && 'border-teal-450 dark:border-teal-350',
          // Unfortunately, we set box-shadow to none in dark mode, so only border will appear in dark mode
          (isWorkspaceDrawerTarget || isHighlighted) &&
            'border-teal-450 shadow-[0px_0px_0px_3px_#A9EDF2] dark:border-teal-350',
          // Don't show crosshair tooltip on panels while dragging or resizing
          isActivePanel || isDragging || over
            ? '!pointer-events-none'
            : 'cursor-default'
        )}
        data-test="workspace-panel-container"
        style={{
          width: panelWidth,
          height: panelHeight,
        }}>
        <WorkspacePanelHeader />
        <div
          ref={domRef}
          className={twMerge(
            'flex flex-col',
            'w-full grow-[1]',
            'justify-center',
            // Applying this to other panels may cause infinite ResizeObserver loop. Please proceed with caution
            // if you are loosening this check.
            (panelType === 'Code Comparer' ||
              panelType === 'Media Browser' ||
              panelType === 'Markdown Panel') &&
              'overflow-auto',
            panelType === WEAVE_TRACE_PANEL_TYPE && 'overflow-y-hidden'
          )}
          id={'editable-panel-' + panel.__id__}>
          {/*
              This is the expensive parts of panel rendering. If it's not in view, don't render it.
              By wrapping it directly here, there's less loader flickering which iprovides a smoother experience when scrolling
              up and down the page.
            */}
          {shouldMountPanel ? (
            <PanelCompRedux
              configMode={false}
              currentHeight={0}
              dimensions={undefined as any}
              panelRef={panelRef}
              readOnly={isReadOnly}
            />
          ) : (
            <div className="h-full w-full" />
          )}
        </div>
      </div>
    </SharedPanelStateContextProvider>
  );
};
